/**
 * What fetchApi resolves to, and how an HTTP answer becomes one. No imports, so tests and
 * other modules can use it without pulling in the auth store.
 */

/** Codes fetchApi sets itself, when no answer came back or it wasn't one the server meant. */
export type ClientErrorCode =
  | 'NETWORK'
  | 'ABORTED'
  | 'BAD_RESPONSE'
  | 'SESSION_EXPIRED'
  | 'SESSION_REFRESH_FAILED'
  | 'REQUEST_IN_PROGRESS'
  /** The browser stopped waiting (lib/time-limit.ts). */
  | 'TIMEOUT';

export const CLIENT_ERROR_CODES: ReadonlySet<string> = new Set<ClientErrorCode>([
  'NETWORK',
  'ABORTED',
  'BAD_RESPONSE',
  'SESSION_EXPIRED',
  'SESSION_REFRESH_FAILED',
  'REQUEST_IN_PROGRESS',
  'TIMEOUT',
]);

/**
 * fetchApi never throws: it resolves to this. Branch on `status` or `code`, never on the text.
 */
export interface ApiResponse<T> {
  data?: T;
  /** The HTTP status. 0 means no response arrived (offline, blocked or cancelled). */
  status: number;
  /** A sentence for people. Set on every failure, never on success. */
  error?: string;
  /** The server's code, or a ClientErrorCode when the server didn't send one. */
  code?: string;
  /** The error body, when the server sent a JSON object. */
  body?: Record<string, unknown>;
  /** Developer text from the server (the catch-all sends it only in development and staging). Never the message. */
  details?: string;
  /** Per-field messages, when the server sent them. */
  fields?: Record<string, string>;
  warnings?: string[];
  preview?: unknown;
}

export const NETWORK_ERROR_TEXT = "Can't reach Software Factory. Check your connection and try again.";

/** The message when the server sent none (an empty body, or a Cloudflare HTML page). */
export function fallbackErrorText(status: number): string {
  if (status === 0) return NETWORK_ERROR_TEXT;
  // Not "Your session has ended": a 401 after a successful refresh leaves the person signed in.
  if (status === 401) return "Software Factory couldn't confirm your sign-in. Reload the page and try again.";
  if (status === 403) return "You don't have permission to do this.";
  if (status === 404) return "Software Factory couldn't find that.";
  if (status === 408 || status === 504 || status === 524) return 'Software Factory took too long to answer. Try again.';
  if (status === 413) return 'That file is too large.';
  if (status === 429) return 'Too many requests. Wait a moment and try again.';
  if (status >= 500) return `Software Factory couldn't finish this (error ${status}). Try again in a moment.`;
  return `Request failed (${status}).`;
}

function parseJson(text: string): unknown {
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringMap(value: unknown): Record<string, string> | undefined {
  if (!isPlainObject(value)) return undefined;
  return Object.values(value).every((v) => typeof v === 'string')
    ? (value as Record<string, string>)
    : undefined;
}

/** One HTTP answer as an ApiResponse. `text` is the raw body. */
export function toApiResponse<T>(status: number, text: string): ApiResponse<T> {
  const parsed = parseJson(text);
  if (status >= 200 && status < 300) return { data: parsed as T | undefined, status };

  const body = isPlainObject(parsed) ? parsed : undefined;
  const serverText =
    typeof body?.error === 'string' && body.error
      ? body.error
      : typeof body?.message === 'string' && body.message
        ? body.message
        : undefined;
  return {
    status,
    error: serverText ?? fallbackErrorText(status),
    // No JSON object means the answer came from something in front of the Worker (a Cloudflare page).
    code: typeof body?.code === 'string' ? body.code : body ? undefined : 'BAD_RESPONSE',
    body,
    details: typeof body?.details === 'string' ? body.details : undefined,
    fields: stringMap(body?.fields),
    warnings: Array.isArray(body?.warnings) ? body.warnings.map(String) : undefined,
    preview: body?.preview,
  };
}

/** No answer at all: fetch rejected (offline, blocked) or the caller aborted. */
export function noResponse<T>(e: unknown): ApiResponse<T> {
  // A DOMException in the browser, which not every engine makes an Error.
  if (typeof e === 'object' && e !== null && (e as { name?: unknown }).name === 'AbortError') {
    return { status: 0, code: 'ABORTED', error: 'The request was cancelled.' };
  }
  return { status: 0, code: 'NETWORK', error: NETWORK_ERROR_TEXT };
}

/** The session is really over: sign the person out. Anything else (0, 5xx) keeps them signed in. */
export function isSessionGone(status: number): boolean {
  return status === 401 || status === 403;
}

/** A success value for local loader updates (`setData`), where no request was made. */
export function apiData<T>(data: T): ApiResponse<T> {
  return { data, status: 200 };
}
